import React from 'react';
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material';
import {
  DeleteOutline,
  DescriptionOutlined,
  EditOutlined,
  OpenInNew,
  PictureAsPdfOutlined,
  SlideshowOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';

/**
 * Per-document actions: preview, edit (in-app or new tab), export, delete.
 * @param {object} props - Props.
 * @param {HTMLElement|null} props.anchorEl - Menu anchor.
 * @param {boolean} props.isPresentation - Offer PowerPoint export instead of Word.
 * @param {boolean} [props.exportDisabled] - Disable export items (an export is running).
 * @param {function(): void} props.onClose - Close the menu.
 * @param {function(): void} props.onPreview - Preview.
 * @param {function(): void} props.onEdit - Open in the editor.
 * @param {function(): void} [props.onOpenNewTab] - Open the editor in a new tab.
 * @param {function(string): void} props.onExport - Export ('word' | 'pdf' | 'pptx').
 * @param {function(): void} props.onDelete - Delete (the caller confirms).
 * @returns {JSX.Element} Menu.
 */
export default function DocumentActionsMenu({
  anchorEl,
  isPresentation,
  exportDisabled = false,
  onClose,
  onPreview,
  onEdit,
  onOpenNewTab,
  onExport,
  onDelete,
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { minWidth: 220 } }}
    >
      <MenuItem onClick={onEdit}>
        <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
        <ListItemText>Open in editor</ListItemText>
      </MenuItem>
      {onOpenNewTab && (
        <MenuItem onClick={onOpenNewTab}>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={onPreview}>
        <ListItemIcon><VisibilityOutlined fontSize="small" /></ListItemIcon>
        <ListItemText>Preview</ListItemText>
      </MenuItem>
      <Divider />
      <ListSubheader disableSticky sx={{ lineHeight: '28px', bgcolor: 'transparent' }}>Export</ListSubheader>
      {isPresentation ? (
        <MenuItem onClick={() => onExport('pptx')} disabled={exportDisabled}>
          <ListItemIcon><SlideshowOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>PowerPoint (.pptx)</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={() => onExport('word')} disabled={exportDisabled}>
          <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Word (.docx)</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={() => onExport('pdf')} disabled={exportDisabled}>
        <ListItemIcon><PictureAsPdfOutlined fontSize="small" /></ListItemIcon>
        <ListItemText>PDF (.pdf)</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={onDelete} sx={{ color: 'error.main' }}>
        <ListItemIcon><DeleteOutline fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
}
